import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Founding Member Badge Component
 * Displays a special badge for early subscribers who joined during the launch period
 * 
 * Variants:
 * - 'badge': Full badge with text (default)
 * - 'icon': Small icon-only version
 * - 'inline': Compact inline version for headers
 */
export function FoundingMemberBadge({ variant = 'badge', className = '' }) {
  const { user } = useAuth();
  
  // Only show if user is a founding member
  if (!user?.founding_member) {
    return null;
  }
  
  if (variant === 'icon') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span 
              className={`inline-flex items-center justify-center w-6 h-6 text-sm cursor-help ${className}`}
              data-testid="founding-member-icon"
            >
              🏛️
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-semibold">Founding Family Member</p>
            <p className="text-xs text-gray-500">Early supporter of Legacy Forge</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  if (variant === 'inline') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span 
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-300 cursor-help ${className}`}
              data-testid="founding-member-badge-inline"
            >
              <span>🏛️</span>
              <span>Founder</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-semibold">Founding Family Member</p>
            <p className="text-xs text-gray-500">Thank you for being an early supporter!</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  // Default 'badge' variant - full badge with shine effect
  return (
    <div 
      className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 text-white font-bold text-sm shadow-lg overflow-hidden ${className}`}
      data-testid="founding-member-badge"
    >
      {/* Animated shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 animate-shine" />
      
      <span className="relative z-10 text-lg">🏛️</span>
      <div className="relative z-10 flex flex-col leading-tight">
        <span className="text-xs opacity-90">Founding</span>
        <span className="text-sm">Family Member</span>
      </div>
    </div>
  );
}

// Add the shine animation to tailwind config or use inline style
// For inline: animation: shine 3s ease-in-out infinite;
// @keyframes shine { 0% { transform: translateX(-100%) skewX(-12deg); } 100% { transform: translateX(200%) skewX(-12deg); } }

export default FoundingMemberBadge;
